import type { ReasonCode } from '../../api';

/**
 * Reason codes whose text interpolates values, and the names of those values.
 *
 * Only names listed here ever reach a text: `reasonMessage` drops everything else the
 * API sends (C2 spec R6, I9). Every code listed here also has a fallback text without
 * placeholders in `Messages.reasonFallback` (spec R8).
 *
 * @example
 * REASON_PARAMS.TELEGRAM_CODE_RATE_LIMITED; // ['minutes']
 */
export const REASON_PARAMS = {
  // Provisional until Q33: the wait in whole minutes, rounded up by the API from Telegram's flood wait.
  TELEGRAM_CODE_RATE_LIMITED: ['minutes'],
} as const satisfies Partial<Record<ReasonCode, readonly string[]>>;

/** A reason code that takes parameters; see {@link REASON_PARAMS}. */
export type ReasonParamCode = keyof typeof REASON_PARAMS;

/**
 * Tells whether a code interpolates parameters.
 *
 * @param code - A known reason code.
 * @returns `true` when `code` is a key of {@link REASON_PARAMS}.
 */
export function isReasonParamCode(code: ReasonCode): code is ReasonParamCode {
  return Object.hasOwn(REASON_PARAMS, code);
}

/**
 * Parameter names that could carry a secret. A parameter with such a name is a review
 * blocker (I9, FXM-01 §7: no password, code, session or login ever shown, even in an error).
 */
export const FORBIDDEN_PARAM_NAME = /password|pwd|session|token|initdata|login|phone|code/i;
